import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import PageHeader from '../../common/PageHeader'
import Section, { SubSection } from '../../common/Section'
import Alert from '../../common/Alert'
import CodeBlock, { SimpleCodeBlock, InlineCode } from '../../common/CodeBlock'
import { ParamTable } from '../../common/Table'
import Table from '../../common/Table'
import { useLocale } from '../../../i18n/useLocale'

export default function ParcelCreation() {
  const { urls, phone, examples, currency, locale, brand } = useLocale()

  return (
    <Box>
      <PageHeader
        title="Parcel Creation"
        description="Learn how to create parcels via REST API or batch file upload."
        breadcrumbs={[
          { label: 'Guides', path: '/guides' },
          { label: 'Parcel Creation' },
        ]}
      />

      <Section title="Overview" divider={false}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {brand.name} offers two methods for creating parcels:
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Card variant="outlined" sx={{ flex: 1 }}>
            <CardContent>
              <Chip label="REST API" color="primary" size="small" sx={{ mb: 1 }} />
              <Typography variant="h6">Single/Batch JSON</Typography>
              <Typography variant="body2" color="text.secondary">
                Create one or more parcels per request via JSON payload (request body limited to
                ~1 MB). Best for real-time order processing.
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ flex: 1 }}>
            <CardContent>
              <Chip label="File Upload" color="secondary" size="small" sx={{ mb: 1 }} />
              <Typography variant="h6">CSV/Excel Import</Typography>
              <Typography variant="body2" color="text.secondary">
                Upload CSV or XLSX files with up to 1,000 parcels. Best for bulk processing.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Section>

      <Section title="REST API: Create Parcels">
        <Typography variant="body1" sx={{ mb: 2 }}>
          The primary endpoint for creating parcels programmatically:
        </Typography>

        <SimpleCodeBlock code="POST /parcel/b2c" />

        <SubSection title="Request Parameters">
          <ParamTable
            params={[
              { name: 'receiverName', type: 'string', required: true, description: 'Full name of the recipient (max 80 chars)' },
              { name: 'receiverEmail', type: 'string', required: true, description: 'Valid email address of the recipient (max 150 chars)' },
              { name: 'receiverPhone', type: 'string', required: true, description: `${locale.country} phone number: ${phone.validFormats}` },
              { name: 'destination', type: 'string', required: false, description: `Code of the destination APM (e.g., "${locale.countryCode.toLowerCase()}0001"). Identifies the whole machine — the system picks an available locker inside it at delivery time.` },
              { name: 'size', type: 'string', required: false, description: 'Parcel size: S (Small), M (Medium), or L (Large). Caution: an invalid or missing value is silently treated as "no size" rather than rejected — always send a valid value.' },
              { name: 'receiverCountry', type: 'string', required: false, description: 'ISO 3166-1 alpha-2 country code of the recipient (max 3 chars)' },
              { name: 'codAmount', type: 'number', required: false, description: `Cash on Delivery amount in ${currency.code}. The platform maximum is configurable (MAX_COD setting) and may change without notice — contact support for the current limit.` },
              { name: 'referenceCode', type: 'string', required: false, description: 'Your internal reference/order ID (max 30 chars, free text — printed on the label)' },
              { name: 'uniqueParcelNumber', type: 'string', required: false, description: 'Your own parcel tracking number/barcode (max 30 chars). Must be globally unique across the platform — a duplicate fails that row. Scannable at all operational touchpoints and usable for tracking.' },
              { name: 'comment', type: 'string', required: false, description: 'Additional notes (max 100 chars)' },
            ]}
          />
        </SubSection>

        <SubSection title="Parcel Sizes">
          <Table
            columns={[
              { key: 'size', header: 'Size', width: '15%' },
              { key: 'dimensions', header: 'Max Dimensions', width: '35%' },
              { key: 'weight', header: 'Max Weight', width: '20%' },
              { key: 'example', header: 'Example Items' },
            ]}
            data={[
              { size: <Chip label="S" size="small" />, dimensions: '365 × 610 × 100 mm', weight: '25 kg', example: 'Documents, small electronics, accessories' },
              { size: <Chip label="M" size="small" color="primary" />, dimensions: '365 × 610 × 200 mm', weight: '25 kg', example: 'Shoes, clothing, medium electronics' },
              { size: <Chip label="L" size="small" color="secondary" />, dimensions: '365 × 610 × 380 mm', weight: '25 kg', example: 'Large items, bulk orders' },
            ]}
          />
        </SubSection>

        <SubSection title="Example Request">
          <CodeBlock
            title="Create Single Parcel"
            examples={[
              {
                language: 'bash',
                label: 'cURL',
                code: `curl -X POST "${urls.apiBase}/parcel/b2c" \\
  -H "Content-Type: application/json" \\
  -H "X-API-KEY: your-api-key" \\
  -d '[{
    "receiverName": "${examples.personName.full}",
    "receiverEmail": "john.doe@example.com",
    "receiverPhone": "${phone.example}",
    "destination": "${locale.countryCode.toLowerCase()}0001",
    "size": "M",
    "codAmount": 1500.00,
    "referenceCode": "ORD-2024-001",
    "comment": "Handle with care"
  }]'`,
              },
              {
                language: 'javascript',
                label: 'Node.js',
                code: `const parcels = [{
  receiverName: '${examples.personName.full}',
  receiverEmail: 'john.doe@example.com',
  receiverPhone: '${phone.example}',
  destination: '${locale.countryCode.toLowerCase()}0001',
  size: 'M',
  codAmount: 1500.00,
  referenceCode: 'ORD-2024-001',
  comment: 'Handle with care'
}];

const response = await fetch('${urls.apiBase}/parcel/b2c', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-KEY': process.env.API_KEY,
  },
  body: JSON.stringify(parcels)
});

const result = await response.json();`,
              },
              {
                language: 'python',
                label: 'Python',
                code: `import requests

parcels = [{
    'receiverName': '${examples.personName.full}',
    'receiverEmail': 'john.doe@example.com',
    'receiverPhone': '${phone.example}',
    'destination': '${locale.countryCode.toLowerCase()}0001',
    'size': 'M',
    'codAmount': 1500.00,
    'referenceCode': 'ORD-2024-001',
    'comment': 'Handle with care'
}]

response = requests.post(
    '${urls.apiBase}/parcel/b2c',
    json=parcels,
    headers={
        'Content-Type': 'application/json',
        'X-API-KEY': os.environ['API_KEY'],
    }
)

result = response.json()`,
              },
            ]}
          />
        </SubSection>

        <SubSection title="Response">
          <SimpleCodeBlock
            code={`{
  "requestedParcelCount": 1,
  "successfulParcelCount": 1,
  "successfulParcels": [
    {
      "parcelNumber": "Q17563175099981697",
      "destination": "${locale.countryCode.toLowerCase()}0001",
      "receiverEmail": "john.doe@example.com",
      "referenceCode": "ORD-2024-001",
      "partnerParcelNumber": null,
      "errors": []
    }
  ],
  "failedParcels": []
}`}
          />
        </SubSection>
      </Section>

      <Section title="Batch File Upload">
        <Typography variant="body1" sx={{ mb: 2 }}>
          For bulk parcel creation, upload a CSV or Excel file:
        </Typography>

        <SimpleCodeBlock code="POST /parcel/file" />

        <SubSection title="File Format Requirements">
          <Typography variant="body1" sx={{ mb: 2 }}>
            Columns are positional, in this exact order (10 columns):{' '}
            <InlineCode>receiverName, receiverEmail, receiverPhone, receiverCountry, destination,
            size, codAmount, referenceCode, uniqueParcelNumber, comment</InlineCode>. The accepted
            header row text is platform-configured and validated strictly — <strong>always start from
            the template file provided during onboarding</strong>; a file with an unrecognized header
            is rejected as a whole (<InlineCode>INVALID_CSV</InlineCode>). Example rows:
          </Typography>
          <SimpleCodeBlock
            code={`${examples.personName.full},john@example.com,${phone.example},${locale.countryCode},${locale.countryCode.toLowerCase()}0001,M,500,ORD-001,TRK-0001,Fragile
Jane Smith,jane@example.com,${phone.example.replace(/\d{4}$/, '6789')},${locale.countryCode},${locale.countryCode.toLowerCase()}0002,S,0,ORD-002,TRK-0002,
Bob Wilson,bob@example.com,${phone.example.replace(/\d{4}$/, '7890')},${locale.countryCode},${locale.countryCode.toLowerCase()}0001,L,1500,ORD-003,TRK-0003,`}
          />
        </SubSection>

        <SubSection title="Finding the APM code for the destination column">
          <Typography variant="body1" sx={{ mb: 2 }}>
            Every row in your CSV needs a <InlineCode>destination</InlineCode> — the
            <strong> APM code</strong> identifying the parcel machine the parcel should be
            delivered to (for example <InlineCode>{locale.countryCode.toLowerCase()}0001</InlineCode>). This is
            the whole-machine identifier; the recipient&apos;s individual locker inside that
            APM is assigned automatically by the system on delivery — you do not (and
            cannot) pick a specific locker. There are three easy ways to look up an APM code:
          </Typography>

          <Box component="ol" sx={{ pl: 3, '& li': { mb: 1.5 } }}>
            <li>
              <strong>Open the APM Finder widget in a browser.</strong>{' '}
              Go to <InlineCode>{urls.widgetBase}</InlineCode>, search by name or
              city, click an APM on the map or in the result list, and copy the displayed
              APM code into the <InlineCode>destination</InlineCode> column.
            </li>
            <li>
              <strong>Embed the APM Finder widget in your own staff tool.</strong>{' '}
              The widget broadcasts a <InlineCode>LOCATION_SELECTED</InlineCode> message
              containing the APM code (in the <InlineCode>boxCode</InlineCode> / <InlineCode>id</InlineCode> fields —
              field names are historical; the value identifies the whole APM, not an
              individual locker). See the{' '}
              <a href="/widgets/apm-finder">APM Finder Widget</a> guide for the message
              format.
            </li>
            <li>
              <strong>Load the full APM list programmatically.</strong>{' '}
              The same data the widget uses is served by{' '}
              <InlineCode>GET {urls.apiBase}/apm</InlineCode> (public, no API key
              required). Cache the result in your system and map your warehouse / pickup
              points to APM codes once, instead of looking them up per upload.
            </li>
          </Box>

          <Alert type="info" title="APM codes are stable">
            An APM code (e.g., <InlineCode>{locale.countryCode.toLowerCase()}0001</InlineCode>) does not change
            once assigned. You can safely store it against your warehouse / pickup-point
            records and reuse it across uploads.
          </Alert>
        </SubSection>

        <SubSection title="Upload Example">
          <CodeBlock
            title="Upload CSV File"
            examples={[
              {
                language: 'bash',
                label: 'cURL',
                code: `curl -X POST "${urls.apiBase}/parcel/file" \\
  -H "X-API-KEY: your-api-key" \\
  -F "file=@parcels.csv"`,
              },
              {
                language: 'javascript',
                label: 'Node.js',
                code: `const FormData = require('form-data');
const fs = require('fs');

const form = new FormData();
form.append('file', fs.createReadStream('parcels.csv'));

const response = await fetch('${urls.apiBase}/parcel/file', {
  method: 'POST',
  headers: {
    'X-API-KEY': process.env.API_KEY,
    ...form.getHeaders()
  },
  body: form
});`,
              },
            ]}
          />
        </SubSection>

        <Alert type="info" title="Supported File Types & Limits">
          CSV (.csv) and Excel (.xlsx) files are supported. Maximum <strong>1,000 data rows</strong>{' '}
          per file (<InlineCode>TOO_MANY_ROWS</InlineCode> beyond that). The multipart field name
          must be <InlineCode>file</InlineCode>.
        </Alert>
      </Section>

      <Section title="Validation Rules">
        <Typography variant="body1" sx={{ mb: 2 }}>
          All parcel data is validated before creation:
        </Typography>

        <SubSection title="Phone Number Format">
          <Typography variant="body2" sx={{ mb: 1 }}>
            {locale.country} phone numbers must match one of these formats:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><InlineCode>{phone.example}</InlineCode> (with country code)</li>
            <li><InlineCode>{phone.example.replace('+', '')}</InlineCode> (without plus)</li>
            <li><InlineCode>{phone.localFormat}</InlineCode> (local format)</li>
          </Box>
        </SubSection>

        <SubSection title="APM Validation">
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <InlineCode>destination</InlineCode> must be the code of a valid, active APM
            (e.g., <InlineCode>{locale.countryCode.toLowerCase()}0001</InlineCode>). Inactive or unknown APM
            codes are rejected and the row is returned in <InlineCode>failedParcels</InlineCode>.
            This value identifies the whole machine — the specific locker compartment is
            assigned by the system at delivery time.
          </Typography>
          <Typography variant="body2">
            <strong>To look up an APM code</strong>, see the{' '}
            &ldquo;Finding the APM code for the destination column&rdquo; subsection in{' '}
            <em>Batch File Upload</em> above — the same lookup options apply to single-parcel
            JSON requests.{' '}
            <strong>For a customer-facing checkout flow</strong>, embed the{' '}
            <a href="/widgets/apm-finder">APM Finder widget</a> and use the code returned in
            the <InlineCode>LOCATION_SELECTED</InlineCode> message.
          </Typography>
        </SubSection>
      </Section>

      <Section title="Error Handling">
        <Typography variant="body1" sx={{ mb: 2 }}>
          When validation fails, parcels are returned in the <InlineCode>failedParcels</InlineCode> array:
        </Typography>

        <SimpleCodeBlock
          code={`{
  "requestedParcelCount": 2,
  "successfulParcelCount": 1,
  "successfulParcels": [...],
  "failedParcels": [
    {
      "destination": "ke9999",
      "receiverEmail": "invalid-email",
      "errors": [
        { "field": "destination", "message": "Invalid APM ID or APM is not active!" },
        { "field": "receiverEmail", "message": "must be a well-formed email address" }
      ]
    }
  ]
}`}
        />

        <Alert type="warning">
          Batch requests are processed per parcel — valid parcels are created even if some rows fail
          validation (the HTTP status stays 200; check <InlineCode>failedParcels</InlineCode>). An
          HTTP <InlineCode>500</InlineCode> means a system error: <strong>nothing from that request
          was created</strong> — retry the whole request or re-upload the file.
        </Alert>
      </Section>
    </Box>
  )
}
